import React, {useEffect, useState} from 'react'
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import close_dialog from '../assets/close_dialog.svg'
import Draggable from 'react-draggable';
import CircularProgress from '@mui/material/CircularProgress';
import { Select } from 'antd';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// ── helpers ──────────────────────────────────────────────────────────────────

const hasTeam = (user, team) =>
  user.StaffTeam && user.StaffTeam.split(',').map(s => s.trim()).includes(team);

const isFixedProjectUser = (user) =>
  hasTeam(user, 'Senior Finance Staff') ||
  hasTeam(user, 'Finance Staff') ||
  hasTeam(user, 'Partners');

const isFixedExpenseUser = (user) =>
  hasTeam(user, 'Senior Finance Staff') ||
  hasTeam(user, 'Finance Staff');

const projectTeamPriority = (user) => {
  if (hasTeam(user, 'Senior Finance Staff')) return 3;
  if (hasTeam(user, 'Partners'))             return 2;
  if (hasTeam(user, 'Finance Staff'))        return 1;
  return 0;
};

const expenseTeamPriority = (user) => {
  if (hasTeam(user, 'Senior Finance Staff')) return 2;
  if (hasTeam(user, 'Finance Staff'))        return 1;
  return 0;
};

const teamLabel = (user) => {
  if (hasTeam(user, 'Senior Finance Staff')) return 'Senior Finance Staff';
  if (hasTeam(user, 'Partners'))             return 'Partners';
  if (hasTeam(user, 'Finance Staff'))        return 'Finance Staff';
  return user.StaffTeam || '';
};

// ── PaperComponent ────────────────────────────────────────────────────────────

function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

// ── Confirmation mini-popup ───────────────────────────────────────────────────

function ConfirmDeletePopup({ user, onConfirm, onCancel }) {
  return (
    <Dialog open={true} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Removal</DialogTitle>
      <DialogContent>
        <Typography style={{ marginBottom: 16 }}>
          Are you sure you want to remove <strong>{user.Name}</strong>?
        </Typography>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outlined" onClick={onCancel}
            style={{ borderColor: '#6394B6', color: '#6394B6' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onConfirm}
            style={{ backgroundColor: '#6394B6', color: '#fff' }}>
            Remove
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function UserPopup({ openPopup, setOpenPopup }) {
  const [activeTab, setActiveTab] = useState(0);

  // ── Project Access state ──
  const [projectData, setProjectData]       = useState(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [addUser, setAddUser]               = useState(false);
  const [projectSearch, setProjectSearch]   = useState('');

  // ── Expense Privilege state ──
  const [expenseData, setExpenseData]         = useState(null);
  const [expenseLoading, setExpenseLoading]   = useState(true);
  const [addExpenseUser, setAddExpenseUser]   = useState(false);
  const [expenseSearch, setExpenseSearch]     = useState('');

  // ── Confirmation state ──
  const [confirmUser, setConfirmUser] = useState(null); // { user, onDelete }

  // ── Tab change ────────────────────────────────────────────────────────────

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setAddUser(false);
    setAddExpenseUser(false);
  };

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchProjectData = async () => {
    setProjectLoading(true);
    const res  = await fetch('/api-dev/expenses/GetUsers', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    setProjectData(data);
    setProjectLoading(false);
  };

  const fetchExpenseData = async () => {
    setExpenseLoading(true);
    const res  = await fetch('/api-dev/expenses/GetExpensePrivilegeUsers', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    setExpenseData(data); // data is directly the array
    setExpenseLoading(false);
  };

  useEffect(() => {
    fetchProjectData();
    fetchExpenseData();
  }, []);

  // ── Derived lists: Project Access ─────────────────────────────────────────

  const allStaff = projectData?.result ?? [];

  const fixedProjectUsers = allStaff
    .filter(isFixedProjectUser)
    .sort((a, b) => {
      const diff = projectTeamPriority(b) - projectTeamPriority(a);
      return diff !== 0 ? diff : a.Name.localeCompare(b.Name);
    });

  const fixedIds = new Set(fixedProjectUsers.map(u => u.StaffID));

  const dynamicAdmins = (projectData?.admins ?? [])
    .filter(admin => !admin.IsDefault && !fixedIds.has(admin.StaffID))
    .map(admin => allStaff.find(u => u.StaffID === admin.StaffID) ?? admin)
    .sort((a, b) => a.Name.localeCompare(b.Name));

  const shownProjectIds = new Set([
    ...fixedProjectUsers.map(u => u.StaffID),
    ...dynamicAdmins.map(u => u.StaffID),
  ]);

  const projectAddOptions = allStaff
    .filter(u => !shownProjectIds.has(u.StaffID))
    .filter(u => !projectSearch || u.Name.toLowerCase().includes(projectSearch.toLowerCase()))
    .map(u => ({ label: `${u.Name} (${u.StaffTeam ?? ''})`, value: u.StaffID }));

  // ── Derived lists: Expense Privilege ─────────────────────────────────────

  const privilegedUsers = expenseData ?? [];

  const enrichedPrivilegedUsers = privilegedUsers.map(pv =>
    allStaff.find(u => u.StaffID === pv.StaffID) ?? pv
  );

  const fixedExpenseUsers = enrichedPrivilegedUsers
    .filter(isFixedExpenseUser)
    .sort((a, b) => {
      const diff = expenseTeamPriority(b) - expenseTeamPriority(a);
      return diff !== 0 ? diff : a.Name.localeCompare(b.Name);
    });

  const fixedExpenseIds = new Set(fixedExpenseUsers.map(u => u.StaffID));

  const dynamicExpenseUsers = privilegedUsers
    .filter(u => !u.IsDefault && !fixedExpenseIds.has(u.StaffID))
    .map(pv => allStaff.find(u => u.StaffID === pv.StaffID) ?? pv)
    .sort((a, b) => a.Name.localeCompare(b.Name));

  const shownExpenseIds = new Set([
    ...fixedExpenseUsers.map(u => u.StaffID),
    ...dynamicExpenseUsers.map(u => u.StaffID),
  ]);

  const expenseAddOptions = allStaff
    .filter(u => !shownExpenseIds.has(u.StaffID))
    .filter(u => !expenseSearch || u.Name.toLowerCase().includes(expenseSearch.toLowerCase()))
    .map(u => ({ label: `${u.Name} (${u.StaffTeam ?? ''})`, value: u.StaffID }));

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddAdmin = async (staffID) => {
    const user = allStaff.find(u => u.StaffID === staffID);
    if (!user) return;
    await fetch('/api-dev/expenses/AddAdmin', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ StaffID: user.StaffID, Name: user.Name, StaffTeam: user.StaffTeam }),
    });
    setAddUser(false);
    fetchProjectData();
  };

  const handleRemoveAdmin = async (user) => {
    await fetch('/api-dev/expenses/RemoveAdmin', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ StaffID: user.StaffID }),
    });
    setConfirmUser(null);
    fetchProjectData();
  };

  const handleAddExpensePrivilege = async (staffID) => {
    const user = allStaff.find(u => u.StaffID === staffID);
    if (!user) return;
    await fetch('/api-dev/expenses/AddExpensePrivilege', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ StaffID: user.StaffID, Name: user.Name, StaffTeam: user.StaffTeam }),
    });
    setAddExpenseUser(false);
    fetchExpenseData();
  };

  const handleRemoveExpensePrivilege = async (user) => {
    await fetch('/api-dev/expenses/RemoveExpensePrivilege', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ StaffID: user.StaffID }),
    });
    setConfirmUser(null);
    fetchExpenseData();
  };

  // ── Sub-components ────────────────────────────────────────────────────────

  const TeamBadge = ({ label }) => (
    <div style={{
      padding: '3px 8px', backgroundColor: '#edf5fa',
      borderRadius: '5px', border: '1px solid #6394B6',
      display: 'flex', alignItems: 'center',
    }}>
      <Typography variant="subtitle2" style={{ color: '#6394B6', lineHeight: 1 }}>
        {label}
      </Typography>
    </div>
  );

  const UserRow = ({ user, badge, onDelete }) => (
    <div style={{
      display: 'flex', flexDirection: 'row', alignItems: 'center',
      padding: '5px', borderBottom: '1px solid #6394B6',
      justifyContent: 'space-between', minHeight: '40px',
    }}>
      <Typography variant="subtitle1">{user.Name}</Typography>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {badge && <TeamBadge label={badge} />}
        {onDelete && (
          <IconButton onClick={() => setConfirmUser({ user, onDelete })}>
            <DeleteOutlineIcon style={{ color: '#6394B6' }} />
          </IconButton>
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {confirmUser && (
        <ConfirmDeletePopup
          user={confirmUser.user}
          onConfirm={() => confirmUser.onDelete(confirmUser.user)}
          onCancel={() => setConfirmUser(null)}
        />
      )}

      <Dialog
        open={openPopup}
        maxWidth="650px"
        style={{ borderRadius: '10px' }}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          <div style={{ display: 'flex' }}>
            <span className='add-user-dialog-title'>User Privileges</span>
            <div style={{ flexGrow: 1, textAlign: 'right' }}>
              <IconButton onClick={() =>
                activeTab === 0 ? setAddUser(v => !v) : setAddExpenseUser(v => !v)
              }>
                <AddIcon style={{ color: '#6394B6' }} />
              </IconButton>
              <IconButton onClick={() => setOpenPopup(false)}>
                <img src={close_dialog} alt="close-dialog" style={{ width: '13px', height: '13px' }} />
              </IconButton>
            </div>
          </div>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="privilege tabs">
              <Tab label="All Project Access" />
              {/*<Tab label="Add Expense Privilege" />*/}
            </Tabs>
          </Box>

          <div style={{ width: '500px' }}>

            {/* ── PROJECT ACCESS TAB ── */}
            {activeTab === 0 && (
              projectLoading ? (
                <Box sx={{ display: 'flex' }} style={{ margin: '40px', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                  {addUser && (
                    <Select
                      showSearch
                      value={null}
                      placeholder="Search staffer by name"
                      defaultActiveFirstOption={false}
                      suffixIcon={null}
                      filterOption={false}
                      style={{ width: '100%', height: '40px', marginBottom: 6 }}
                      onSearch={setProjectSearch}
                      onChange={handleAddAdmin}
                      notFoundContent={null}
                      options={projectAddOptions}
                    />
                  )}

                  {fixedProjectUsers.map(user => (
                    <UserRow
                      key={user.StaffID}
                      user={user}
                      badge={teamLabel(user)}
                      onDelete={null}
                    />
                  ))}

                  {dynamicAdmins.map(user => (
                    <UserRow
                      key={user.StaffID}
                      user={user}
                      badge={null}
                      onDelete={handleRemoveAdmin}
                    />
                  ))}
                </div>
              )
            )}

            {/* ── EXPENSE PRIVILEGE TAB ── */}
            {activeTab === 1 && (
              expenseLoading || expenseData === null ? (
                <Box sx={{ display: 'flex' }} style={{ margin: '40px', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                  {addExpenseUser && (
                    <Select
                      showSearch
                      value={null}
                      placeholder="Search staffer by name"
                      defaultActiveFirstOption={false}
                      suffixIcon={null}
                      filterOption={false}
                      style={{ width: '100%', height: '40px', marginBottom: 6 }}
                      onSearch={setExpenseSearch}
                      onChange={handleAddExpensePrivilege}
                      notFoundContent={null}
                      options={expenseAddOptions}
                    />
                  )}

                  {fixedExpenseUsers.map(user => (
                    <UserRow
                      key={user.StaffID}
                      user={user}
                      badge={teamLabel(user)}
                      onDelete={null}
                    />
                  ))}

                  {dynamicExpenseUsers.map(user => (
                    <UserRow
                      key={user.StaffID}
                      user={user}
                      badge={user.StaffTeam ?? null}
                      onDelete={handleRemoveExpensePrivilege}
                    />
                  ))}
                </div>
              )
            )}

          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UserPopup;